// ChatGPT version of a VanillaJS custom event system that mimics jQuery
/**
 * Attach one or more event listeners to an EventTarget.
 * Works for both native and custom events.
 *
 * @param {EventTarget} target - The target to listen on (window, document, element, etc.)
 * @param {string} eventNames - One or more space-separated event names
 * @param {(detail: any, event: Event) => void} handler - Callback function
 * @param {AddEventListenerOptions | boolean} [options] - Optional listener options
 * @returns {() => void} Function to remove the attached listeners
 */
export function on(target, eventNames, handler, options = {}) {
    if (!target || typeof target.addEventListener !== 'function')
        throw new TypeError('on(target, ...) expects an EventTarget');

    if (typeof eventNames !== 'string' || !eventNames.trim())
        throw new TypeError('eventNames must be a non-empty string');

    if (typeof handler !== 'function')
        throw new TypeError('handler must be a function');

    const names = eventNames.split(/\s+/).filter(Boolean)

    const wrapped = event => {
        if ('detail' in event) {
            handler(event, event.detail)
        } else {
            handler(event, undefined)
        }
    }

    for (const name of names) {
        target.addEventListener(name, wrapped, options)
    }        

    return () => {
        for (const name of names) {
            target.removeEventListener(name, wrapped, options)
        }            
    }
}

/**
 * Dispatch a custom event with optional detail payload.
 *
 * @param {EventTarget} target - The target to dispatch the event on
 * @param {string} eventName - The event name
 * @param {any} [detail] - Optional payload passed as event.detail
 * @param {{ bubbles?: boolean, cancelable?: boolean, composed?: boolean }} [options]
 * @returns {boolean} False if preventDefault() was called on a cancelable event
 */
export function trigger(target, eventName, detail, options = {}) {
    if (!target || typeof target.dispatchEvent !== 'function')
        throw new TypeError('trigger(target, ...) expects an EventTarget');

    if (typeof eventName !== 'string' || !eventName.trim())
        throw new TypeError('eventName must be a non-empty string');

    const event = new CustomEvent(eventName, {
        detail,
        bubbles: options.bubbles ?? true,
        cancelable: options.cancelable ?? true,
        composed: options.composed ?? false
    })

    return target.dispatchEvent(event)
}